/**
 * Tokenizer and Pratt parser for the expression language.
 *
 * Grammar (loosest to tightest binding):
 *   compare   :=  sum (('<'|'<='|'>'|'>='|'=='|'!=') sum)?
 *   sum       :=  product (('+'|'-') product)*
 *   product   :=  unary (('*'|'/') unary)*
 *   unary     :=  ('-'|'+')* power
 *   power     :=  atom ('^' unary)?            -- right associative
 *   atom      :=  number | ident | ident '(' args ')' | '(' compare ')'
 */

import {
  type Node,
  type BinaryOp,
  type CompareOp,
  FUNCTIONS,
  CONSTANTS,
  isFunctionName,
} from './ast.ts';

export class ParseError extends Error {
  constructor(
    message: string,
    readonly position: number,
  ) {
    super(message);
    this.name = 'ParseError';
  }
}

type TokenType = 'num' | 'ident' | 'op' | 'lparen' | 'rparen' | 'comma' | 'eof';

interface Token {
  type: TokenType;
  text: string;
  value?: number;
  pos: number;
}

const OPERATOR_CHARS = new Set(['+', '-', '*', '/', '^', '<', '>', '=', '!']);

function tokenize(src: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < src.length) {
    const ch = src[i];

    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i++;
      continue;
    }

    if (ch >= '0' && ch <= '9') {
      const start = i;
      while (i < src.length && src[i] >= '0' && src[i] <= '9') i++;
      if (src[i] === '.') {
        i++;
        while (i < src.length && src[i] >= '0' && src[i] <= '9') i++;
      }
      if (src[i] === 'e' || src[i] === 'E') {
        const save = i;
        i++;
        if (src[i] === '+' || src[i] === '-') i++;
        if (src[i] >= '0' && src[i] <= '9') {
          while (i < src.length && src[i] >= '0' && src[i] <= '9') i++;
        } else {
          i = save; // not an exponent after all, e.g. `2e` followed by nothing
        }
      }
      const text = src.slice(start, i);
      tokens.push({ type: 'num', text, value: Number(text), pos: start });
      continue;
    }

    // A leading '.' digit form such as `.5`
    if (ch === '.' && src[i + 1] >= '0' && src[i + 1] <= '9') {
      const start = i;
      i++;
      while (i < src.length && src[i] >= '0' && src[i] <= '9') i++;
      const text = src.slice(start, i);
      tokens.push({ type: 'num', text, value: Number(text), pos: start });
      continue;
    }

    if (/[A-Za-z_]/.test(ch)) {
      const start = i;
      while (i < src.length && /[A-Za-z0-9_]/.test(src[i])) i++;
      tokens.push({ type: 'ident', text: src.slice(start, i), pos: start });
      continue;
    }

    if (ch === '(') {
      tokens.push({ type: 'lparen', text: ch, pos: i++ });
      continue;
    }
    if (ch === ')') {
      tokens.push({ type: 'rparen', text: ch, pos: i++ });
      continue;
    }
    if (ch === ',') {
      tokens.push({ type: 'comma', text: ch, pos: i++ });
      continue;
    }

    if (OPERATOR_CHARS.has(ch)) {
      const two = src.slice(i, i + 2);
      if (two === '<=' || two === '>=' || two === '==' || two === '!=') {
        tokens.push({ type: 'op', text: two, pos: i });
        i += 2;
        continue;
      }
      if (ch === '=' || ch === '!') {
        throw new ParseError(`Unexpected '${ch}' (did you mean '${ch}='?)`, i);
      }
      tokens.push({ type: 'op', text: ch, pos: i++ });
      continue;
    }

    throw new ParseError(`Unexpected character '${ch}'`, i);
  }

  tokens.push({ type: 'eof', text: '', pos: src.length });
  return tokens;
}

class Parser {
  private pos = 0;

  constructor(private readonly tokens: Token[]) {}

  private peek(): Token {
    return this.tokens[this.pos];
  }

  private next(): Token {
    return this.tokens[this.pos++];
  }

  private expect(type: TokenType, what: string): Token {
    const t = this.peek();
    if (t.type !== type) {
      throw new ParseError(`Expected ${what} but found ${t.text || 'end of expression'}`, t.pos);
    }
    return this.next();
  }

  parseExpression(): Node {
    const node = this.parseCompare();
    const t = this.peek();
    if (t.type !== 'eof') {
      throw new ParseError(`Unexpected '${t.text}'`, t.pos);
    }
    return node;
  }

  private parseCompare(): Node {
    const left = this.parseSum();
    const t = this.peek();
    if (
      t.type === 'op' &&
      (t.text === '<' || t.text === '<=' || t.text === '>' || t.text === '>=' ||
        t.text === '==' || t.text === '!=')
    ) {
      this.next();
      const right = this.parseSum();
      return { kind: 'compare', op: t.text as CompareOp, left, right };
    }
    return left;
  }

  private parseSum(): Node {
    let left = this.parseProduct();
    for (;;) {
      const t = this.peek();
      if (t.type === 'op' && (t.text === '+' || t.text === '-')) {
        this.next();
        const right = this.parseProduct();
        left = { kind: 'binary', op: t.text as BinaryOp, left, right };
      } else {
        return left;
      }
    }
  }

  private parseProduct(): Node {
    let left = this.parseUnary();
    for (;;) {
      const t = this.peek();
      if (t.type === 'op' && (t.text === '*' || t.text === '/')) {
        this.next();
        const right = this.parseUnary();
        left = { kind: 'binary', op: t.text as BinaryOp, left, right };
      } else {
        return left;
      }
    }
  }

  private parseUnary(): Node {
    const t = this.peek();
    if (t.type === 'op' && (t.text === '-' || t.text === '+')) {
      this.next();
      const operand = this.parseUnary();
      return t.text === '-' ? { kind: 'neg', operand } : operand;
    }
    return this.parsePower();
  }

  private parsePower(): Node {
    const base = this.parseAtom();
    const t = this.peek();
    if (t.type === 'op' && t.text === '^') {
      this.next();
      // Right-associative, and binds tighter than unary minus on the left but
      // looser on the right: -2^2 = -4, 2^-1 = 0.5.
      const exponent = this.parseUnary();
      return { kind: 'binary', op: '^', left: base, right: exponent };
    }
    return base;
  }

  private parseAtom(): Node {
    const t = this.next();

    if (t.type === 'num') {
      return { kind: 'num', value: t.value! };
    }

    if (t.type === 'lparen') {
      const inner = this.parseCompare();
      this.expect('rparen', "')'");
      return inner;
    }

    if (t.type === 'ident') {
      if (this.peek().type === 'lparen') {
        this.next();
        const args: Node[] = [];
        if (this.peek().type !== 'rparen') {
          for (;;) {
            args.push(this.parseCompare());
            if (this.peek().type === 'comma') {
              this.next();
              continue;
            }
            break;
          }
        }
        this.expect('rparen', "')'");

        if (t.text === 'if') {
          if (args.length !== 3) {
            throw new ParseError(`if() takes 3 arguments, got ${args.length}`, t.pos);
          }
          return { kind: 'if', cond: args[0], then: args[1], otherwise: args[2] };
        }

        // The allowlist check that makes an imported project safe to compile.
        if (!isFunctionName(t.text)) {
          throw new ParseError(`Unknown function '${t.text}'`, t.pos);
        }
        const arity = FUNCTIONS[t.text];
        if (args.length !== arity) {
          throw new ParseError(
            `${t.text}() takes ${arity} argument${arity === 1 ? '' : 's'}, got ${args.length}`,
            t.pos,
          );
        }
        return { kind: 'call', name: t.text, args };
      }

      if (t.text in CONSTANTS) {
        return { kind: 'num', value: CONSTANTS[t.text] };
      }
      return { kind: 'ref', name: t.text };
    }

    throw new ParseError(`Unexpected ${t.text || 'end of expression'}`, t.pos);
  }
}

/** Parse an expression, throwing ParseError with a source position on failure. */
export function parse(src: string): Node {
  if (src.trim() === '') throw new ParseError('Empty expression', 0);
  return new Parser(tokenize(src)).parseExpression();
}
